import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FormDialog } from '@ui/FormDialog';
import { GhostButton } from '@ui/GhostButton';

/** Outcome of the follow-up question after a scale was set on the active page. */
export type ApplyToAllPagesChoice = 'cancel' | 'active' | 'all';

type Resolver = (choice: ApplyToAllPagesChoice) => void;

/** Set while an {@link ApplyToAllPagesDialogHost} is mounted. */
let requestDialog: ((resolve: Resolver) => void) | undefined;

/**
 * Ask whether the scale just measured on the active page should propagate to
 * every page. Imperative like `confirm()` so calibration hooks can await it
 * without rendering the dialog themselves. Resolves `'cancel'` when no
 * {@link ApplyToAllPagesDialogHost} is mounted (e.g. the report preview map).
 */
export const askApplyToAllPages = (): Promise<ApplyToAllPagesChoice> => {
  const request = requestDialog;
  if (!request) return Promise.resolve('cancel');
  return new Promise<ApplyToAllPagesChoice>((resolve) => request(resolve));
};

/**
 * Mount once inside the Design view: renders the dialog opened by
 * {@link askApplyToAllPages}.
 */
export const ApplyToAllPagesDialogHost: React.FC = () => {
  const [resolver, setResolver] = useState<Resolver | undefined>();
  const resolverRef = useRef<Resolver | undefined>(undefined);
  resolverRef.current = resolver;

  useEffect(() => {
    requestDialog = (resolve) => {
      // A pending question is superseded rather than stacked.
      resolverRef.current?.('cancel');
      setResolver(() => resolve);
    };
    return () => {
      requestDialog = undefined;
      resolverRef.current?.('cancel');
    };
  }, []);

  const close = useCallback((choice: ApplyToAllPagesChoice) => {
    resolverRef.current?.(choice);
    setResolver(undefined);
  }, []);

  return (
    <FormDialog
      open={resolver !== undefined}
      onClose={() => close('cancel')}
      title="Set on all pages?"
      actions={
        // Cancel left, the two apply choices right — same as the import wizard.
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
          }}
        >
          <GhostButton onClick={() => close('cancel')}>Cancel</GhostButton>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <GhostButton onClick={() => close('active')}>
              This page only
            </GhostButton>
            <GhostButton onClick={() => close('all')}>Set on all</GhostButton>
          </Box>
        </Box>
      }
    >
      <Typography variant="body2">
        Apply this scale to all pages in the document?
      </Typography>
    </FormDialog>
  );
};
